import { log } from "helpers/logger"

/**
 * Event Emitter for cross-feature communication in Dashboard
 *
 * This pattern is used for lateral communication between
 * independent features (e.g., AI Teammate → Dashboard Modules).
 */

type EventHandler<T = unknown> = (data: T) => void
type CleanupFunction = () => void

interface OnOptions {
  replay?: boolean
}

class EventEmitter {
  private events: Map<string, EventHandler[]>
  private lastEmitted: Map<string, unknown>

  constructor() {
    this.events = new Map()
    this.lastEmitted = new Map()
  }

  /**
   * Subscribe to an event
   * @param event - Event name
   * @param handler - Handler function
   * @param options - Optional. Set replay: true to receive the last emitted value immediately if the event was already broadcast.
   * @returns Cleanup function to unsubscribe
   */
  on<T = unknown>(event: string, handler: EventHandler<T>, options?: OnOptions): CleanupFunction {
    if (options?.replay) {
      if (this.lastEmitted.has(event)) {
        const last = this.lastEmitted.get(event)
        try {
          // Replay cache stores unknown payloads across all events, so we cast
          // back to the subscriber's expected payload type at call time.
          handler(last as T)
        } catch (error) {
          log(`Error in replay handler for "${event}": ${error instanceof Error ? error.message : String(error)}`)
        }
      }
    }

    if (!this.events.has(event)) {
      this.events.set(event, [])
    }
    const handlers = this.events.get(event)
    if (handlers) {
      // The internal registry is intentionally untyped by event name; handlers
      // for each event can have different payload types, so we store as unknown.
      handlers.push(handler as EventHandler)
    }

    // Return cleanup function for useEffect
    return () => {
      const handlers = this.events.get(event)
      if (!handlers) return

      // Same reason as above: remove from the untyped internal handler array.
      const index = handlers.indexOf(handler as EventHandler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  /**
   * Emit an event to all subscribers
   * @param event - Event name
   * @param data - Event data
   */
  emit<T = unknown>(event: string, data: T): void {
    this.lastEmitted.set(event, data)
    const handlers = this.events.get(event) || []
    // Iterate over a shallow copy to avoid issues when handlers remove themselves
    // during iteration (which would cause array mutations and skip subsequent handlers)
    const handlersCopy = [...handlers]
    handlersCopy.forEach((handler) => {
      try {
        handler(data)
      } catch (error) {
        log(`Error in event handler for "${event}": ${error instanceof Error ? error.message : String(error)}`)
      }
    })
  }

  /**
   * Subscribe to an event only once
   * @param event - Event name
   * @param handler - Handler function
   * @returns Cleanup function
   */
  once<T = unknown>(event: string, handler: EventHandler<T>): CleanupFunction {
    const wrapper = (data: T) => {
      handler(data)
      cleanup()
    }
    const cleanup = this.on(event, wrapper)
    return cleanup
  }

  /**
   * Remove all handlers for an event, or all events if no event specified.
   * Also clears replay data so late subscribers do not receive stale values.
   * @param event - Optional event name. If omitted, clears all events.
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.events.delete(event)
      this.lastEmitted.delete(event)
    } else {
      this.events.clear()
      this.lastEmitted.clear()
    }
  }

  /**
   * Get count of handlers for an event (used in unit tests)
   * @param event - Event name
   * @returns Number of handlers
   */
  listenerCount(event: string): number {
    const handlers = this.events.get(event)
    return handlers ? handlers.length : 0
  }
}

// Singleton instance for dashboard events
export const dashboardEvents = new EventEmitter()

/**
 * Event names for dashboard communication
 * Using namespaced pattern: feature:action:status
 */
export const DASHBOARD_EVENTS = {
  // Artifact pinning events
  ARTIFACT_PIN_REQUESTED: "artifact:pin:requested",
  ARTIFACT_PINNED: "artifact:pinned",
  ARTIFACT_PIN_FAILED: "artifact:pin:failed",
  ARTIFACT_PIN_LIMIT_STATUS: "artifact:pin:limit:status",

  // Module lifecycle events (for future use)
  MODULE_ADDED: "module:added",
  MODULE_REMOVED: "module:removed",
  MODULE_UPDATED: "module:updated",
} as const

export type DashboardEventName = typeof DASHBOARD_EVENTS[keyof typeof DASHBOARD_EVENTS]
